import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import Loader from '../../components/Loader.jsx';
import Message from '../../components/Message.jsx';
import {
  useAcknowledgeIncidentMutation,
  useCreateIncidentMutation,
  useGetAdminAuditLogsQuery,
  useGetFraudEventsQuery,
  useGetIncidentsQuery,
  useGetMonitoringSummaryQuery,
  useRefreshIncidentsMutation,
  useResolveIncidentMutation,
  useSetFraudEventStatusMutation,
} from '../../slices/adminOpsApiSlice.js';

const StatusBadge = ({ value }) => {
  const status = String(value || '').toUpperCase();
  const cls =
    status === 'CRITICAL' || status === 'FAILED' || status === 'OPEN'
      ? 'bg-rose-100 text-rose-700'
      : status === 'WARN' || status === 'ACKNOWLEDGED'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{status || 'N/A'}</span>;
};

function AdminOperationsScreen() {
  const [auditQuery, setAuditQuery] = useState('');
  const [incidentTitle, setIncidentTitle] = useState('');
  const [incidentMessage, setIncidentMessage] = useState('');
  const [incidentSeverity, setIncidentSeverity] = useState('WARN');

  const {
    data: auditData,
    isLoading: auditLoading,
    error: auditError,
    refetch: refetchAudit,
  } = useGetAdminAuditLogsQuery({ page: 1, pageSize: 25, q: auditQuery });
  const {
    data: fraudData,
    isLoading: fraudLoading,
    error: fraudError,
    refetch: refetchFraud,
  } = useGetFraudEventsQuery({ page: 1, pageSize: 25 });
  const {
    data: monitoring,
    isLoading: monitoringLoading,
    error: monitoringError,
    refetch: refetchMonitoring,
  } = useGetMonitoringSummaryQuery();
  const {
    data: incidentsData,
    isLoading: incidentsLoading,
    error: incidentsError,
    refetch: refetchIncidents,
  } = useGetIncidentsQuery();

  const [setFraudStatus, { isLoading: settingFraudStatus }] = useSetFraudEventStatusMutation();
  const [refreshIncidents, { isLoading: refreshingIncidents }] = useRefreshIncidentsMutation();
  const [createIncident, { isLoading: creatingIncident }] = useCreateIncidentMutation();
  const [ackIncident, { isLoading: ackingIncident }] = useAcknowledgeIncidentMutation();
  const [resolveIncident, { isLoading: resolvingIncident }] = useResolveIncidentMutation();

  const busy = settingFraudStatus || refreshingIncidents || creatingIncident || ackingIncident || resolvingIncident;

  const cards = useMemo(
    () => [
      { label: 'Stripe webhook failures (24h)', value: monitoring?.failedStripeWebhooks24h ?? '-' },
      { label: 'PayPal webhook failures (24h)', value: monitoring?.failedPaypalWebhooks24h ?? '-' },
      { label: 'Open fraud events', value: monitoring?.openFraudEvents ?? '-' },
      { label: 'Open incidents', value: monitoring?.openIncidents ?? '-' },
      { label: 'Reconciliation drift orders', value: monitoring?.reconciliationDriftOrders ?? '-' },
      { label: 'Payout failed items', value: monitoring?.payoutFailedItems ?? '-' },
    ],
    [monitoring]
  );

  const refreshAll = async () => {
    try {
      await refreshIncidents().unwrap();
      await Promise.all([refetchMonitoring(), refetchIncidents(), refetchFraud(), refetchAudit()]);
      toast.success('Monitoring and incidents refreshed');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to refresh incidents');
    }
  };

  const createIncidentHandler = async (e) => {
    e.preventDefault();
    if (!incidentTitle.trim()) {
      toast.error('Incident title is required');
      return;
    }
    try {
      await createIncident({
        title: incidentTitle.trim(),
        message: incidentMessage.trim(),
        severity: incidentSeverity,
      }).unwrap();
      setIncidentTitle('');
      setIncidentMessage('');
      setIncidentSeverity('WARN');
      refetchIncidents();
      toast.success('Incident created');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to create incident');
    }
  };

  const markFraudReviewed = async (id) => {
    try {
      await setFraudStatus({ id, status: 'REVIEWED' }).unwrap();
      refetchFraud();
      toast.success('Fraud event marked as reviewed');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update fraud event');
    }
  };

  const acknowledgeIncidentHandler = async (id) => {
    try {
      await ackIncident(id).unwrap();
      refetchIncidents();
      toast.success('Incident acknowledged');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to acknowledge incident');
    }
  };

  const resolveIncidentHandler = async (id) => {
    try {
      await resolveIncident(id).unwrap();
      refetchIncidents();
      toast.success('Incident resolved');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to resolve incident');
    }
  };

  return (
    <div className="space-y-6">
      <section className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Admin Operations</h1>
          <p className="text-sm text-slate-600">Audit logs, fraud review, monitoring, and incident panel.</p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          disabled={busy}
          className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {refreshingIncidents ? 'Refreshing...' : 'Refresh Incidents'}
        </button>
      </section>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {monitoringLoading ? (
          <Loader />
        ) : monitoringError ? (
          <Message variant="danger">{monitoringError?.data?.message || 'Failed to load monitoring summary'}</Message>
        ) : (
          cards.map((card) => (
            <article key={card.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-600">{card.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{card.value}</p>
            </article>
          ))
        )}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-xl font-semibold text-slate-900">Incident Panel</h2>
          <form onSubmit={createIncidentHandler} className="mb-4 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <input
              type="text"
              placeholder="Incident title"
              value={incidentTitle}
              onChange={(e) => setIncidentTitle(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
            <textarea
              rows={2}
              placeholder="Message"
              value={incidentMessage}
              onChange={(e) => setIncidentMessage(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
            <div className="flex items-center gap-2">
              <select
                value={incidentSeverity}
                onChange={(e) => setIncidentSeverity(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="INFO">INFO</option>
                <option value="WARN">WARN</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
              <button
                type="submit"
                disabled={creatingIncident}
                className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Add Incident
              </button>
            </div>
          </form>

          {incidentsLoading ? (
            <Loader />
          ) : incidentsError ? (
            <Message variant="danger">{incidentsError?.data?.message || 'Failed to load incidents'}</Message>
          ) : (
            <div className="max-h-[420px] space-y-2 overflow-y-auto">
              {(incidentsData?.incidents || []).map((incident) => (
                <article key={incident._id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{incident.title}</p>
                    <div className="flex items-center gap-2">
                      <StatusBadge value={incident.severity} />
                      <StatusBadge value={incident.status} />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{incident.code}</p>
                  <p className="mt-1 text-sm text-slate-700">{incident.message}</p>
                  <div className="mt-2 flex gap-2">
                    {incident.status !== 'ACKNOWLEDGED' && incident.status !== 'RESOLVED' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => acknowledgeIncidentHandler(incident._id)}
                        className="rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                      >
                        Acknowledge
                      </button>
                    )}
                    {incident.status !== 'RESOLVED' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => resolveIncidentHandler(incident._id)}
                        className="rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-xl font-semibold text-slate-900">Fraud Review Dashboard</h2>
            {fraudLoading ? (
              <Loader />
            ) : fraudError ? (
              <Message variant="danger">{fraudError?.data?.message || 'Failed to load fraud events'}</Message>
            ) : (
              <div className="max-h-[220px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="py-2">Event</th>
                      <th className="py-2">Status</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fraudData?.events || []).map((event) => (
                      <tr key={event._id} className="border-b border-slate-100">
                        <td className="py-2">
                          <p className="font-medium text-slate-800">{event.eventType}</p>
                          <p className="text-xs text-slate-500">{event.route}</p>
                        </td>
                        <td className="py-2">
                          <StatusBadge value={event.status} />
                        </td>
                        <td className="py-2 text-right">
                          {event.status === 'OPEN' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => markFraudReviewed(event._id)}
                              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Mark reviewed
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-xl font-semibold text-slate-900">Centralized Admin Audit Log</h2>
            <div className="mb-3">
              <input
                type="text"
                value={auditQuery}
                onChange={(e) => setAuditQuery(e.target.value)}
                placeholder="Filter by action/path/admin email..."
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            {auditLoading ? (
              <Loader />
            ) : auditError ? (
              <Message variant="danger">{auditError?.data?.message || 'Failed to load audit logs'}</Message>
            ) : (
              <div className="max-h-[220px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="py-2">Action</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData?.logs || []).map((log) => (
                      <tr key={log._id} className="border-b border-slate-100">
                        <td className="py-2">
                          <p className="font-medium text-slate-800">{log.action}</p>
                          <p className="text-xs text-slate-500">{log.method} {log.path}</p>
                        </td>
                        <td className="py-2">
                          <StatusBadge value={log.success ? 'OK' : 'FAILED'} />
                        </td>
                        <td className="py-2 text-xs text-slate-500">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdminOperationsScreen;

